import * as yup from 'yup';
import styled, { withTheme } from 'styled-components';
import { useState, useCallback, useMemo } from 'react';

import {
  FovFieldError,
  FovInput,
  FovModal,
  VerticalSpacer,
} from 'components';
import { FovButton } from 'components/generic';
import { Formik, Field } from 'formik';
import { ButtonContainer } from 'pages/ModelCanvas/SideMenus/Publish/publishStyles';

import { addCreditsAdmin } from 'store/actions/admin';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { getColor } from 'utils';

const MODES = { SET: 'set', ADD: 'add', REMOVE: 'remove' };

const modeLabel = { [MODES.SET]: 'Set', [MODES.ADD]: 'Add', [MODES.REMOVE]: 'Remove' };
const inputLabel = {
    [MODES.SET]: 'New Credits Value',
    [MODES.ADD]: 'Amount to Add',
    [MODES.REMOVE]: 'Amount to Remove',
};

const InputContainer = styled('div')`
    max-width: 25rem;
`;

const ModeRow = styled('div')`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
`;

const ModeButton = styled(FovButton)`
    width: fit-content;
    padding: 0 1rem;
    height: 2rem;
    font-size: 0.85rem;
    background-color: ${props => props.$active ? getColor(props, 'accent') : getColor(props, 'mediumGrey')};
    color: ${props => props.$active ? getColor(props, 'onAccent') : getColor(props, 'onPrimary')};
    &:hover {
        background-color: ${props => props.$active ? getColor(props, 'accentHover') : getColor(props, 'darkGrey')};
    }
`;

const InfoText = styled('div')`
    font-size: 0.9rem;
    color: ${props => getColor(props, 'onPrimary')};
    margin-bottom: 0.75rem;
`;

const ResultText = styled('div')`
    font-size: 0.85rem;
    color: ${props => getColor(props, 'darkGrey')};
    margin-top: 0.25rem;
`;

const AddCreditsForm = withTheme(props => {
    const { theme, isVisible, onClose, user } = props;
    const dispatch = useDispatch();
    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;
    const [ mode, setMode ] = useState(MODES.SET);

    const userDataStore = useSelector(state => state.userData);
    const selectedTheme = userDataStore.userPreferences.theme;

    const currentCredits = Number(user?.meta_data?.find(d => d.key === 'av_points')?.value || 0);

    const schema = useMemo(() => {
        let amountField = yup.number()
            .typeError('Must be a number.')
            .required('Required.')
            .min(0, 'Must be 0 or greater.');

        if (mode === MODES.REMOVE) {
            amountField = amountField.max(currentCredits, `Cannot exceed current balance (${currentCredits}).`);
        }

        return yup.object().shape({ amount: amountField });
    }, [ mode, currentCredits ]);

    const computeNewCredits = useCallback(amount => {
        const num = Number(amount);
        if (isNaN(num)) return currentCredits;
        if (mode === MODES.SET) return num;
        if (mode === MODES.ADD) return currentCredits + num;
        if (mode === MODES.REMOVE) return currentCredits - num;
        return currentCredits;
    }, [ mode, currentCredits ]);

    const handleSubmit = useCallback((values, handlers) => {
        const newCredits = computeNewCredits(values.amount);
        dispatch(addCreditsAdmin(token, user.id, newCredits))
            .finally(() => {
                handlers.setSubmitting(false);
                onClose();
            });
    }, [ user, computeNewCredits ]);

    const handleClose = () => {
        setMode(MODES.SET);
        onClose();
    };

    const userName = [ user?.first_name, user?.last_name ].filter(Boolean).join(' ') || user?.email || '';

    return (
        <FovModal
            title={`Manage Credits — ${userName}`}
            visible={isVisible}
            onClose={handleClose}
        >
            <ModeRow>
                {Object.values(MODES).map(m => (
                    <ModeButton
                        key={m}
                        theme={theme}
                        $selectedTheme={selectedTheme}
                        $active={mode === m}
                        onClick={() => setMode(m)}
                    >
                        {modeLabel[m]}
                    </ModeButton>
                ))}
            </ModeRow>

            <InfoText theme={theme} $selectedTheme={selectedTheme}>
                Current balance: <strong>{currentCredits}</strong> credits
            </InfoText>

            <Formik
                key={mode}
                initialValues={{ amount: '' }}
                validationSchema={schema}
                onSubmit={(values, handlers) => handleSubmit(values, handlers)}
                validateOnChange={true}
            >
                {form => (
                    <>
                        {inputLabel[mode]}
                        <Field type='number' name='amount'>
                            {data => (
                                <InputContainer>
                                    <FovInput
                                        type='number'
                                        value={data.field.value}
                                        placeholder='0'
                                        {...data.field}
                                        onChange={e => form.setFieldValue(data.field.name, e.target.value)}
                                    />
                                </InputContainer>
                            )}
                        </Field>
                        <FovFieldError name='amount' />

                        {form.values.amount !== '' && !isNaN(Number(form.values.amount)) && (
                            <ResultText theme={theme} $selectedTheme={selectedTheme}>
                                New balance: <strong>{computeNewCredits(form.values.amount)}</strong> credits
                            </ResultText>
                        )}

                        <VerticalSpacer size={16} />

                        <ButtonContainer>
                            <FovButton
                                type='submit'
                                disabled={form.isSubmitting}
                                onClick={form.handleSubmit}
                            >
                                {modeLabel[mode]} Credits
                            </FovButton>
                        </ButtonContainer>
                    </>
                )}
            </Formik>
        </FovModal>
    );
});

export { AddCreditsForm };
